import { PrinterProfile } from '../../types/printerProfile';
import { PortedTicketData as TicketData } from './types';
import {
  buildTicketPlan,
  joinTicketLines,
  planLineToMono,
  sanitizeCp1252,
  tileSeparator,
  PlanLine,
} from './ticketTestPlan';
import {
  RASTER_WIDTH_58MM,
  RASTER_WIDTH_80MM,
  RasterLineInput,
  isRasterUsable,
  rasterizeToImgLines,
} from './escposRaster';
import { buildNoAnchorTicket } from './ticketFormatterNoAnchor';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * TEST 1 — « RASTER » : le ticket ENTIER part en image `<img>` (GS v 0)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * HYPOTHÈSE TESTÉE : aucune règle de réécriture de l'app Android ne peut atteindre ce
 * qui n'est pas du texte. Le pré-traitement de `getAsyncEscPosPrinter()` inspecte des
 * CHAÎNES (`line.trim()`, `cleanedLine.matches(".*x\\d+.*")`, `contains("taille:")`) ; une
 * ligne `[L]<img>1d7630…</img>` ne matche rien de tout ça et traverse le bloc intacte,
 * jusqu'à `PrinterTextParserColumn` qui la reconnaît comme image et écrit les octets
 * `GS v 0` bruts sur la connexion.
 *
 * CE QUE CE MODULE PROUVE : la fidélité ABSOLUE. Ce qui est composé est ce qui sort —
 * colonnes au pixel, gras réel, accents indépendants de la page de code de l'imprimante,
 * et immunité totale au verrou `ARTICLES`/`x<chiffres>`. C'est la référence contre
 * laquelle les trois autres modules se jugent.
 *
 * CE QUE ÇA COÛTE : ~18 400 caractères hexadécimaux par bande de 128 lignes de pixels,
 * soit typiquement 130 à 200 Ko pour un ticket 80 mm complet. Ça passe par
 * `window.android.print(texte)` sans transformation (`printQueue.stripMarkers()` ne
 * touche que les marqueurs `[…]` et les `\n{3,}`), mais l'impression est plus lente et
 * la mémoire du clone bas de gamme plus sollicitée qu'avec du texte.
 * Le TEST 2 (hybride) existe pour mesurer ce coût : il ne met en image QUE la zone
 * verrouillée, soit ~10 % du volume.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ REFUS EXPLICITE SUR STAR ET SUNMI — pas un simple avertissement
 * ───────────────────────────────────────────────────────────────────────────────
 * La balise `<img>` n'existe QUE dans le parseur DantSu, donc que sur `usb`, `bluetooth`
 * et `tcp`. Sur `sunmi` le texte part brut, sur `star_usb` les balises sont supprimées,
 * sur `star` le texte est dessiné sur un Canvas : dans les trois cas l'hexadécimal
 * s'imprimerait EN CLAIR, sur des mètres de papier.
 * `isRasterUsable()` lit le type natif dans les identifiants d'imprimante enregistrés par
 * le routage (`type-adresse`, MainActivity:5754) et REFUSE le rendu image si une cible
 * incompatible y figure. Dans ce cas — comme en l'absence de `<canvas>` — ce module se
 * rabat automatiquement sur le rendu TEXTE du TEST 4 : le ticket sort correct, il perd
 * seulement le bénéfice de l'image. On n'émet JAMAIS d'hexadécimal « au cas où ».
 */

/** 576 px dès qu'on est en 80 mm, 384 px sinon. Voir le bloc LARGEUR d'`escposRaster`. */
export const rasterWidthPxFor = (charsPerLine: number): number =>
  charsPerLine >= 40 ? RASTER_WIDTH_80MM : RASTER_WIDTH_58MM;

const widthOf = (profile?: PrinterProfile): number => {
  const w = Number(profile?.config?.width);
  return Number.isFinite(w) && w >= 24 ? Math.min(64, Math.floor(w)) : 48;
};

/**
 * Facteur d'agrandissement choisi par l'exploitant (`config.rasterScale`, modules image
 * uniquement). `1` = taille de base (défaut) ; plafonné à 2 pour rester lisible.
 */
export const rasterScaleOf = (profile?: PrinterProfile): number => {
  const s = Number(profile?.config?.rasterScale);
  return Number.isFinite(s) && s > 1 ? Math.min(2, s) : 1;
};

/**
 * Mise en évidence des infos clés (n° de commande / type / table) demandée par l'exploitant :
 * activée par DÉFAUT (`config.rasterHighlightKeyInfo` absent ou vrai), désactivable en réglant
 * explicitement le flag à `false` dans le profil.
 */
export const rasterHighlightKeyInfoOf = (profile?: PrinterProfile): boolean =>
  profile?.config?.rasterHighlightKeyInfo !== false;

/**
 * Caractères par ligne EFFECTIFS une fois l'échelle appliquée. Agrandir le texte = composer
 * MOINS de caractères sur la MÊME largeur physique (la police calculée par `fitFontSize`
 * grossit d'autant). Planché à 24 (borne basse de `buildTicketPlan`) : au-delà les libellés
 * d'articles se replient proprement (wrap) au lieu d'être tronqués ou de déborder du papier.
 */
export const scaledCharsPerLine = (baseWidth: number, scale: number): number =>
  Math.max(24, Math.round(baseWidth / (scale > 1 ? scale : 1)));

/**
 * Motif de séparateur retenu pour un profil : `config.separator` s'il est DÉFINI (même vide,
 * = choix « Aucun » de l'exploitant), sinon le séparateur avancé d'en-tête, sinon `=` (défaut
 * historique du raster). Renvoyé BRUT (non tuilé).
 */
export const resolveSeparatorPattern = (profile?: PrinterProfile): string => {
  const cfg = profile?.config;
  if (cfg && typeof cfg.separator === 'string') return cfg.separator;
  const adv = cfg?.advancedTemplate?.separators?.afterHeader;
  if (typeof adv === 'string') return adv;
  return '=';
};

/**
 * Version TEXTE-SÛRE du séparateur, pour les parties NON-image de l'hybride (qui repartent en
 * texte vers le pont) : assainie CP1252 puis tuilée ; si elle retombe sur un run de ≥24 tirets
 * (qui armerait le dédoublonnage natif de deux lignes identiques) ou devient vide alors que
 * l'exploitant voulait un séparateur, on se rabat sur `=` pleine largeur. Le choix « Aucun »
 * (motif vide explicite) est respecté.
 */
export const textSafeSeparator = (rawPattern: string, width: number): string => {
  if (rawPattern === '') return '';
  const tiled = tileSeparator(sanitizeCp1252(rawPattern), width);
  if (tiled === '' || /-{24,}/.test(tiled)) return '='.repeat(width);
  return tiled;
};

export interface RasterSliceOptions {
  /** Caractères par ligne visés (déjà mis à l'échelle exploitant). */
  charsPerLine: number;
  /** Largeur PHYSIQUE de l'image en px (576/384) — jamais dérivée de `charsPerLine` ici. */
  widthPx: number;
  /** Émettre les balises de routage `[CAT:…]` en texte entre les images. */
  emitMarkers: boolean;
  /** Séparateur exploitant DÉJÀ tuilé à `charsPerLine`, honoré tel quel dans l'image. */
  separator?: string;
  /**
   * Mettre en évidence les infos clés (lignes `emphasis` : n° de commande, type, table) :
   * rendu 2× + vidéo inversée. `undefined`/`true` = activé (défaut demandé par l'exploitant) ;
   * `false` = ces lignes s'impriment normalement (taille de base, pas d'inversion).
   */
  highlightKeyInfo?: boolean;
}

/**
 * Met une tranche de plan en image, en préservant les balises de routage EN TEXTE.
 *
 * ⚠️ Les `[CAT:…]` ne peuvent PAS entrer dans l'image : `printQueue.splitContentByCategory()`
 * les cherche dans le TEXTE pour ventiler les tickets cuisine. Une balise noyée dans un
 * bitmap ferait tomber tout le routage en silence. On découpe donc la tranche en
 * sous-blocs autour des marqueurs : chaque sous-bloc devient sa propre suite de bandes,
 * et les marqueurs restent des lignes de texte entre deux images.
 *
 * ⚠️ `widthPx` est la largeur PHYSIQUE du papier et reste INDÉPENDANTE de `charsPerLine` :
 * l'agrandissement joue sur `charsPerLine` (moins de car. = police plus grosse), jamais sur
 * la largeur de l'image — l'en-tête GS v 0 xL/xH doit rester 72 (80 mm) / 48 (58 mm) octets.
 *
 * Renvoie `null` si le rendu image échoue : l'appelant DOIT alors se rabattre sur le texte.
 */
export const rasterizePlanSlice = (
  lines: PlanLine[],
  opts: RasterSliceOptions
): string[] | null => {
  const { charsPerLine, widthPx, emitMarkers, separator } = opts;
  const highlightKeyInfo = opts.highlightKeyInfo !== false;
  const out: string[] = [];
  let buffer: RasterLineInput[] = [];

  const flush = (): boolean => {
    if (!buffer.length) return true;
    const imgLines = rasterizeToImgLines(buffer, { widthPx, charsPerLine });
    if (!imgLines) return false;
    out.push(...imgLines);
    buffer = [];
    return true;
  };

  for (const ln of lines) {
    if (ln.k === 'marker') {
      if (!flush()) return null;
      if (emitMarkers) out.push(ln.t);
      continue;
    }
    // INFO CLÉ (n° de commande, type, table) mise en évidence : rendue 2× en VIDÉO INVERSÉE,
    // centrée en pixels, sur UNE seule ligne (`noWrap` → auto-fit, jamais de coupure en deux).
    // On court-circuite `planLineToMono` : c'est ici qu'on décide de l'inversion, pilotée par la
    // case de l'exploitant (`highlightKeyInfo`).
    //
    // ⚠️ NOIRCEUR DES BANDEAUX : chaque bandeau est émis comme SA PROPRE image (on vide le buffer
    // AVANT et APRÈS). Ainsi son pavé noir tient dans UNE seule bande GS v 0 (hauteur < 128), donc
    // aucune frontière de bande ne le traverse — pas de raccord/reset de tête au milieu du noir,
    // l'aplat reste continu et uniforme.
    if (highlightKeyInfo && ln.k === 'center' && ln.emphasis) {
      if (!flush()) return null;
      buffer.push({ text: ln.t.trim(), bold: true, scale: 2, center: true, reverse: true, noWrap: true });
      if (!flush()) return null;
      continue;
    }
    // Le séparateur exploitant est passé au rendu mono : dans l'image on l'honore tel quel.
    const mono = planLineToMono(ln, charsPerLine, separator);
    if (!mono) continue;
    // Le nom de l'établissement passe en 2× (`big`), COMME les bandeaux d'infos clés mais SANS
    // vidéo inversée. `size = fontPx * 2` est bien appliqué par le rendu (voir `escposRaster`) ;
    // une image n'a pas la contrainte des balises `<font size='big'>` (qui feraient déborder une
    // ligne texte) et, s'il est trop long, le rendu le fait passer À LA LIGNE en gardant la
    // grande taille (jamais de rétrécissement). Centrage EN PIXELS (`center`), jamais en espaces.
    const scale = ln.k === 'center' && ln.big ? 2 : 1;
    buffer.push({ text: mono.text, bold: mono.bold, scale, center: mono.center });
  }

  return flush() ? out : null;
};

/**
 * Nombre de lignes vides de marge en HAUT du ticket image.
 * Mis à 0 à la demande du restaurateur : il ne voulait AUCUN espace blanc en tête
 * (il trouvait qu'il y en avait trop). Avec 0, `rasterTopMargin` ne rasterise aucune
 * ligne et renvoie `[]` → le prépend n'ajoute rien dans les deux formateurs.
 */
const RASTER_TOP_MARGIN_LINES = 0;

/**
 * Petite MARGE BLANCHE en HAUT du ticket image : quelques lignes vides RASTERISÉES (une image
 * blanche), pour que le contenu ne démarre pas collé au bord du papier — symétrique de l'espace
 * laissé en bas. Rendue en IMAGE (et non en lignes texte) car une ligne texte vide en tête serait
 * supprimée par le pont (`renderNativeColumns` retire les blancs de début), alors qu'une image
 * blanche fait bien avancer le papier. Renvoie `[]` si le rendu image n'est pas possible.
 */
export const rasterTopMargin = (charsPerLine: number, widthPx: number): string[] => {
  const blanks: PlanLine[] = Array.from(
    { length: RASTER_TOP_MARGIN_LINES },
    () => ({ k: 'blank' as const })
  );
  const img = rasterizePlanSlice(blanks, { charsPerLine, widthPx, emitMarkers: false });
  return img || [];
};

export const buildRasterTicket = (data: TicketData, profile?: PrinterProfile): string => {
  const baseWidth = widthOf(profile);
  // Largeur PHYSIQUE du papier : figée par le profil AVANT toute mise à l'échelle.
  const paperPx = rasterWidthPxFor(baseWidth);
  const emitMarkers = profile?.config?.showCategories !== false;

  // REFUS avant toute composition : Star / Sunmi / pas de canvas → repli texte intégral.
  if (!isRasterUsable()) return buildNoAnchorTicket(data, profile);

  // Échelle exploitant : on réduit les caractères/ligne, ce qui grossit la police ET fait
  // wrapper les libellés longs (budget libellé = width/2) au lieu de les tronquer.
  const charsPerLine = scaledCharsPerLine(baseWidth, rasterScaleOf(profile));
  const plan = buildTicketPlan(data, {
    width: charsPerLine,
    // Forme la plus dangereuse en texte (`Pizza x2`) : c'est justement ce que l'image
    // doit faire passer intact. Le même libellé, en texte, perdrait son prix.
    quantityStyle: 'x-suffix',
    emitCategoryMarkers: emitMarkers,
  });

  // Séparateur exploitant, honoré TEL QUEL dans l'image (pixels — aucune contrainte du pont).
  const separator = tileSeparator(resolveSeparatorPattern(profile), plan.width);

  const lines = rasterizePlanSlice(plan.lines, {
    charsPerLine: plan.width,
    widthPx: paperPx,
    emitMarkers,
    separator,
    highlightKeyInfo: rasterHighlightKeyInfoOf(profile),
  });
  if (!lines) return buildNoAnchorTicket(data, profile);

  // Marge blanche en haut (symétrique de l'espace du bas), puis le contenu.
  return joinTicketLines([...rasterTopMargin(plan.width, paperPx), ...lines]);
};
